import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import Pager from '../components/Pager';
import { readQuery } from '../query';
import { COPENHAGEN_TIME_NOTE } from '../../common/copenhagenTime';
import {
  ATTENTION, STATUSES, STATUS_HINT, JobStatus, cph, duration, copenhagenMidnight, nextDay
} from './DeviceJobsShared';

/**
 * Device jobs — bookings and cancellations that run on a physical device (the
 * official Playtomic app on an iPhone), and the runners next to the devices.
 *
 * Read-only on purpose: a job is never retried or resolved from here. What the
 * page is for is spotting the one state that needs a person — **Unknown**: a
 * runner took the job and never said how it ended, so the booking may exist on
 * the venue's side while the signup says it failed. Those come first in the
 * counters and are tinted in the table.
 *
 * The filters live in the URL, so a filtered view can be linked (the signup page
 * of a job links back here with `signupId`). The list refreshes itself every 15
 * seconds while the tab is visible, because Queued and Claimed only mean
 * something while they are current.
 */

const REFRESH_MS = 15000;
const PER_PAGE = 50;

const DeviceJobsInternal = ({ api }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = readQuery(location.search);

  const status = query.status || '';
  const action = query.action || '';
  const signupId = query.signupId || '';
  const integrationId = query.integrationId || '';
  const from = query.from || '';
  const to = query.to || '';
  const page = Math.max(1, Number(query.page) || 1);
  const perPage = Number(query.count) || PER_PAGE;

  const [jobs, setJobs] = useState(null);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [runners, setRunners] = useState(null);
  const [failed, setFailed] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loadedAt, setLoadedAt] = useState(null);

  const setQuery = changes => {
    const next = { ...query, ...changes };
    if (!('page' in changes)) delete next.page;
    Object.keys(next).forEach(key => {
      if (next[key] === '' || next[key] === undefined || next[key] === null) delete next[key];
    });
    const search = new URLSearchParams(next).toString();
    navigate(`/next/device-jobs${search ? `?${search}` : ''}`);
  };

  const load = useCallback(async () => {
    const filters = {
      Status: status || undefined,
      Action: action || undefined,
      SignupId: signupId || undefined,
      IntegrationId: integrationId || undefined,
      From: copenhagenMidnight(from),
      To: to ? copenhagenMidnight(nextDay(to)) : undefined,
      Page: page,
      Count: perPage
    };

    try {
      const [list, counts, runnerList] = await Promise.all([
        api.deviceJobs.searchDeviceJobs(filters),
        api.deviceJobs.getDeviceJobSummary(),
        api.deviceJobs.getDeviceRunners()
      ]);
      setJobs(list?.items ?? []);
      setTotal(list?.totalCount ?? 0);
      setSummary(counts ?? null);
      setRunners(Array.isArray(runnerList) ? runnerList : []);
      setFailed(null);
      setLoadedAt(new Date());
    } catch (error) {
      setFailed(error?.message || 'Could not load the device jobs.');
    }
  }, [api, status, action, signupId, integrationId, from, to, page, perPage]);

  useEffect(() => {
    setJobs(null);
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const counts = summary?.countsByStatus || {};
  const pages = Math.max(1, Math.ceil(total / perPage));
  const filtered = !!(status || action || signupId || integrationId || from || to);

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Device jobs</h1>
          <p className="next-muted">
            Bookings that run on a phone, through the venue&apos;s official app.
            {loadedAt && ` Updated ${loadedAt.toLocaleTimeString('en-GB', { timeZone: 'Europe/Copenhagen' })}.`}
          </p>
        </div>
        <div className="next-page__actions">
          <label className="next-filter next-filter--check">
            <input type="checkbox" checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} />
            <span>Auto-refresh</span>
          </label>
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      {failed && (
        <div className="next-note next-note--error">
          {failed}{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      )}

      {counts.Unknown > 0 && (
        <div className="next-note next-note--warn">
          <strong>{counts.Unknown} job{counts.Unknown === 1 ? '' : 's'} Unknown.</strong>{' '}
          {STATUS_HINT.Unknown}{' '}
          {status !== 'Unknown' && (
            <button type="button" className="next-btn next-btn--sm" onClick={() => setQuery({ status: 'Unknown' })}>
              Show them
            </button>
          )}
        </div>
      )}

      {/* Every status, the three that may need a person first. Clicking one filters. */}
      <div className="next-kpis">
        {[...ATTENTION, ...STATUSES.filter(x => !ATTENTION.includes(x))].map(name => (
          <div
            role="button"
            tabIndex={0}
            key={name}
            className="next-kpi"
            style={{ cursor: 'pointer', outline: status === name ? '2px solid var(--ws-accent, currentColor)' : undefined }}
            onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && setQuery({ status: status === name ? '' : name })}
            title={STATUS_HINT[name]}
            onClick={() => setQuery({ status: status === name ? '' : name })}
            aria-pressed={status === name}
          >
            <span className="next-kpi__label">{status === name ? `▸ ${name}` : name}</span>
            <span className="next-kpi__value">
              {summary ? (counts[name] ?? 0).toLocaleString() : <span className="next-kpi__pending">—</span>}
            </span>
            <span
              className={`next-kpi__hint${name === 'Unknown' && counts[name] > 0 ? ' next-kpi__hint--bad' : ''}${name === 'Queued' && counts[name] > 0 ? ' next-kpi__hint--warn' : ''}`}
            >
              {name === 'Queued' && summary?.oldestQueuedAt
                ? `oldest ${cph(summary.oldestQueuedAt)}`
                : 'all time'}
            </span>
          </div>
        ))}
      </div>

      <section className="next-card">
        <div className="next-card__head">
          <h2>Runners</h2>
          {summary && (
            <span className="next-muted">
              Offline after {summary.runnerOfflineAfterMinutes} min without polling, unless running a job
              (run timeout {summary.runTimeoutMinutes} min, queue timeout {summary.queueTimeoutMinutes} min).
            </span>
          )}
        </div>
        <div className="next-table-wrap">
          <table className="next-table next-table--stack">
            <thead>
              <tr>
                <th>Runner</th>
                <th>State</th>
                <th>Last seen</th>
                <th>Running</th>
                <th>Scripts</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              {runners === null && (
                <tr><td colSpan={6} className="next-muted next-table__note">Loading…</td></tr>
              )}
              {runners !== null && runners.length === 0 && (
                <tr><td colSpan={6} className="next-muted next-table__note">No runner has ever polled the queue.</td></tr>
              )}
              {(runners ?? []).map(runner => (
                <tr key={runner.name}>
                  <td data-label="Runner"><strong>{runner.name}</strong></td>
                  <td data-label="State">
                    <span className={`next-pill next-pill--${runner.online ? 'live' : 'off'}`}>
                      {runner.online ? (runner.runningJobId ? 'Online · busy' : 'Online') : 'Offline'}
                    </span>
                  </td>
                  <td data-label="Last seen" className="next-nowrap">{cph(runner.lastSeenAt)}</td>
                  <td data-label="Running">
                    {runner.runningJobId
                      ? (
                        <Link className="next-link" to={`/next/device-job/${runner.runningJobId}`}>
                          since {cph(runner.runningSince)}
                        </Link>
                      )
                      : <span className="next-muted">—</span>}
                  </td>
                  <td data-label="Scripts">{(runner.scripts || []).join(', ') || <span className="next-muted">—</span>}</td>
                  <td data-label="Version">{runner.version || <span className="next-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="next-card">
        <div className="next-filters__row">
          <label className="next-filter">
            <span className="next-filter__label">Status</span>
            <select className="next-input" value={status} onChange={event => setQuery({ status: event.target.value })}>
              <option value="">All</option>
              {STATUSES.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label className="next-filter">
            <span className="next-filter__label">Action</span>
            <select className="next-input" value={action} onChange={event => setQuery({ action: event.target.value })}>
              <option value="">All</option>
              <option value="Book">Book</option>
              <option value="Cancel">Cancel</option>
            </select>
          </label>
          <label className="next-filter">
            <span className="next-filter__label">Signup id</span>
            <input
              className="next-input"
              defaultValue={signupId}
              key={`s${signupId}`}
              placeholder="Paste a signup id"
              onBlur={event => event.target.value.trim() !== signupId && setQuery({ signupId: event.target.value.trim() })}
            />
          </label>
          <label className="next-filter">
            <span className="next-filter__label">Integration id</span>
            <input
              className="next-input"
              defaultValue={integrationId}
              key={`i${integrationId}`}
              placeholder="Paste an integration id"
              onBlur={event => event.target.value.trim() !== integrationId && setQuery({ integrationId: event.target.value.trim() })}
            />
          </label>
          <label className="next-filter">
            <span className="next-filter__label">Created from</span>
            <input className="next-input" type="date" value={from} onChange={event => setQuery({ from: event.target.value })} />
          </label>
          <label className="next-filter">
            <span className="next-filter__label">Created to</span>
            <input className="next-input" type="date" value={to} onChange={event => setQuery({ to: event.target.value })} />
          </label>
          {filtered && (
            <div className="next-filters__buttons">
              <button type="button" className="next-btn" onClick={() => navigate('/next/device-jobs')}>Clear</button>
            </div>
          )}
        </div>
      </section>

      <section className="next-card">
        <div className="next-card__head">
          <h2>Jobs</h2>
          <span className="next-muted">{jobs === null ? 'Loading…' : `${total.toLocaleString()} ${filtered ? 'matching' : 'in total'}`}</span>
        </div>
        <div className="next-table-wrap">
          <table className="next-table next-table--wide next-table--stack">
            <thead>
              <tr>
                <th>Created</th>
                <th>Status</th>
                <th>Action</th>
                <th>Venue</th>
                <th>Booking</th>
                <th>Runner</th>
                <th className="next-table__right">Wait / run</th>
                <th>Reference / error</th>
              </tr>
            </thead>
            <tbody>
              {jobs !== null && jobs.length === 0 && (
                <tr>
                  <td colSpan={8} className="next-muted next-table__note">
                    {filtered ? 'No job matches these filters.' : 'No device job yet.'}
                  </td>
                </tr>
              )}
              {(jobs ?? []).map(job => {
                const wait = duration(job.createdAt, job.claimedAt);
                const run = duration(job.claimedAt, job.finishedAt);
                return (
                  <tr key={job.id}>
                    <td data-label="Created" className="next-nowrap">
                      <Link className="next-link" to={`/next/device-job/${job.id}`}>{cph(job.createdAt)}</Link>
                    </td>
                    <td data-label="Status"><JobStatus status={job.status} /></td>
                    <td data-label="Action">{job.action}</td>
                    <td data-label="Venue">
                      <span className="next-stack">
                        <span className="next-stack__main">{job.facilityName || job.productName || '—'}</span>
                        <span className="next-stack__sub">
                          {[job.facilityName && job.productName, job.systemType || job.script].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </td>
                    <td data-label="Booking">
                      {job.signupId ? (
                        <span className="next-stack">
                          <Link className="next-link" to={`/next/signup/${job.signupId}`}>
                            {job.activityStartsAt ? cph(job.activityStartsAt) : 'Signup'}
                          </Link>
                          <span className="next-stack__sub next-break">{job.userEmail || ''}</span>
                        </span>
                      ) : <span className="next-muted">—</span>}
                    </td>
                    <td data-label="Runner">{job.claimedBy || <span className="next-muted">—</span>}</td>
                    <td data-label="Wait / run" className="next-table__right next-nowrap">
                      {wait || '—'} / {run || '—'}
                    </td>
                    <td data-label="Reference / error" className="next-break">
                      {job.error
                        ? <span title={job.error}>{job.error.length > 120 ? `${job.error.slice(0, 120)}…` : job.error}</span>
                        : (job.reference || <span className="next-muted">—</span>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pager
          page={page}
          pages={pages}
          hasNext={page < pages}
          perPage={perPage}
          onPerPageChange={size => setQuery({ count: String(size) })}
          onChange={next => setQuery({ page: String(next) })}
        />
      </section>

      <p className="next-muted next-page__footnote">{COPENHAGEN_TIME_NOTE}</p>
    </div>
  );
};

DeviceJobsInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const DeviceJobs = withApi(DeviceJobsInternal);

DeviceJobs.requiresLogin = true;

export default DeviceJobs;
